import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SignupStep } from "./SignupStep";
import { SkillsAutocomplete, type SkillSearchResult } from "@/components/skills/SkillsAutocomplete";
import { useSuggestSkillAction } from "@/hooks/useSkillPickerActions";
import { consultantApplicationApi } from "@/lib/api/consultant-application";
import { CvUpload } from "./CvUpload";
import { SignupAcknowledgements } from "./SignupAcknowledgements";
import { allAcknowledged, buildSubmission } from "@/lib/signupConsents";
import { User, ExternalLink, Clock, DollarSign, FileText, Briefcase } from "lucide-react";
import { cn } from "@/lib/utils";
import { RATE_CURRENCIES, toRateCurrency, type RateCurrency } from "@/lib/rateCurrency";
import { EMPLOYMENT_INTEREST_QUESTION } from "@/lib/employmentInterest";
import { LINKEDIN_PROFILE_EXAMPLE, linkedInProfileError } from "@/lib/validation/linkedin";
import type {
  ApplicationSubmitRequest,
  SignupConsentDocument,
  Skill,
} from "@/types/consultant-application";

// Bio, years of experience and hourly rate are required by the backend's
// completeness check (#891); the numeric fields arrive as strings from the
// inputs and "0" is a valid value.
const profileSkillsSchema = z.object({
  bio: z
    .string()
    .trim()
    .min(1, "Please write a short professional bio")
    .max(1000, "Bio must not exceed 1000 characters"),
  // Optional; any pasted LinkedIn profile link shape is accepted and
  // normalised on save (#870).
  linkedinUrl: z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      const message = linkedInProfileError(val);
      if (message) {
        ctx.addIssue({ code: "custom", message });
      }
    }),
  yearsExperience: z
    .string()
    .min(1, "Years of experience is required")
    .refine(
      val => !isNaN(Number(val)) && Number(val) >= 0 && Number(val) <= 50,
      "Years of experience must be between 0 and 50"
    ),
  hourlyRate: z
    .string()
    .min(1, "Hourly rate is required")
    .refine(
      val => !isNaN(Number(val)) && Number(val) >= 0 && Number(val) <= 10000,
      "Hourly rate must be between 0 and 10000"
    ),
});

type ProfileSkillsFormData = z.infer<typeof profileSkillsSchema>;

interface ProfileSkillsStepProps {
  formData: {
    bio: string;
    linkedinUrl: string;
    yearsExperience: number | null;
    hourlyRate: number | null;
    /** Effective currency: the applicant's choice or the country default (#883). */
    hourlyRateCurrency: RateCurrency;
    interestedInEmployment: boolean;
    selectedSkills: Skill[];
    cvUrl: string | null;
  };
  sessionId: string | null;
  isLoading: boolean;
  error?: string;
  onFormChange: (
    data: Partial<
      ProfileSkillsFormData & {
        selectedSkills: Skill[];
        cvUrl: string | null;
        hourlyRateCurrency: RateCurrency;
        interestedInEmployment: boolean;
      }
    >
  ) => void;
  onSearchSkills: (query: string) => Promise<SkillSearchResult[]>;
  /** Signup acknowledgements served by the backend (#863); null while loading. */
  consentDocuments: SignupConsentDocument[] | null;
  consentsLoading: boolean;
  consentsError: string | null;
  onRetryConsents: () => void;
  /** Called with the acknowledgement body once everything is valid and ticked. */
  onNext: (acknowledgements: ApplicationSubmitRequest) => void;
  onBack: () => void;
}

export const ProfileSkillsStep: React.FC<ProfileSkillsStepProps> = ({
  formData,
  sessionId,
  isLoading,
  error,
  onFormChange,
  onSearchSkills,
  consentDocuments,
  consentsLoading,
  consentsError,
  onRetryConsents,
  onNext,
  onBack,
}) => {
  // Deliberately not persisted: the applicant ticks the boxes on the step
  // they submit from, every time (#863).
  const [accepted, setAccepted] = React.useState<Record<string, boolean>>({});
  const acknowledged = allAcknowledged(consentDocuments, accepted);

  // Applicants can suggest a skill the catalogue lacks; an admin reviews it
  // and, if accepted, it is added to the application (backend#992).
  const submitSuggestion = React.useCallback(
    (text: string) => {
      if (!sessionId) {
        return Promise.reject(new Error("No application session"));
      }
      return consultantApplicationApi.suggestSkill(sessionId, text);
    },
    [sessionId]
  );
  const loadSuggestions = React.useMemo(
    () => (sessionId ? () => consultantApplicationApi.getSkillSuggestions(sessionId) : null),
    [sessionId]
  );
  const { action: suggestAction, pending: pendingSuggestions } = useSuggestSkillAction(
    submitSuggestion,
    loadSuggestions
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ProfileSkillsFormData>({
    resolver: zodResolver(profileSkillsSchema),
    defaultValues: {
      bio: formData.bio || "",
      linkedinUrl: formData.linkedinUrl || "",
      yearsExperience: formData.yearsExperience !== null ? String(formData.yearsExperience) : "",
      hourlyRate: formData.hourlyRate !== null ? String(formData.hourlyRate) : "",
    },
    mode: "onChange",
  });

  // Watch form changes and update parent state
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch() is not React Compiler-compatible
    const subscription = watch(value => {
      onFormChange({
        ...value,
        selectedSkills: formData.selectedSkills, // Keep existing skills
      } as Partial<ProfileSkillsFormData & { selectedSkills: Skill[] }>);
    });
    return () => subscription.unsubscribe();
  }, [watch, onFormChange, formData.selectedSkills]);

  const onSubmit = () => {
    if (!consentDocuments || !acknowledged) {
      return;
    }
    onNext(buildSubmission(consentDocuments));
  };

  const handleSkillsChange = (skills: Skill[]) => {
    onFormChange({
      selectedSkills: skills,
    });
  };

  const bioValue = watch("bio") || "";
  const bioLength = bioValue.length;
  const maxBioLength = 1000;

  // Form is valid if basic validation passes, at least one skill is selected
  // and both privacy/confidentiality acknowledgements are ticked (#863).
  const canProceed = isValid && formData.selectedSkills.length > 0 && acknowledged;

  return (
    <SignupStep
      title="Profile & Skills"
      description="Tell us about your professional background and expertise. This information helps clients find and evaluate your services."
      canGoNext={canProceed}
      canGoBack={true}
      isLoading={isLoading}
      isLastStep={true}
      onNext={handleSubmit(onSubmit)}
      onBack={onBack}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Professional Bio */}
        <div className="space-y-2">
          <Label htmlFor="bio" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Professional Bio *
          </Label>
          <Textarea
            id="bio"
            placeholder="Describe your professional background, experience, and what makes you unique as a consultant. Mention your key strengths, industry experience, and the value you bring to clients..."
            {...register("bio")}
            className={cn(
              "min-h-[120px] resize-y transition-all duration-200",
              errors.bio && "border-destructive focus-visible:ring-destructive"
            )}
            disabled={isLoading}
          />
          <div className="flex justify-between items-center">
            {errors.bio ? (
              <p className="text-sm text-destructive font-medium">{errors.bio.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Required — this is the first thing clients read about you
              </p>
            )}
            <span
              className={cn(
                "text-xs",
                bioLength > maxBioLength * 0.9
                  ? "text-orange-600 dark:text-orange-400"
                  : "text-muted-foreground"
              )}
            >
              {bioLength}/{maxBioLength}
            </span>
          </div>
        </div>

        {/* Skills Selection */}
        <SkillsAutocomplete
          selectedSkills={formData.selectedSkills}
          onSkillsChange={handleSkillsChange}
          onSearchSkills={onSearchSkills}
          maxSkills={10}
          placeholder="Search for skills (e.g., React, Python, Project Management)..."
          noMatchAction={sessionId ? suggestAction : undefined}
          pendingSuggestions={pendingSuggestions}
        />

        {/* Experience and Rate Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Years of Experience */}
          <div className="space-y-2">
            <Label htmlFor="yearsExperience" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Years of Experience *
            </Label>
            <Input
              id="yearsExperience"
              type="number"
              min="0"
              max="50"
              placeholder="e.g., 5"
              {...register("yearsExperience")}
              className={cn(
                "transition-all duration-200",
                errors.yearsExperience && "border-destructive focus-visible:ring-destructive"
              )}
              disabled={isLoading}
            />
            {errors.yearsExperience && (
              <p className="text-sm text-destructive font-medium">
                {errors.yearsExperience.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">Total years of professional experience</p>
          </div>

          {/* Hourly Rate */}
          <div className="space-y-2">
            <Label htmlFor="hourlyRate" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Hourly Rate *
            </Label>
            {/* The currency is required with the rate (backend#883). It is
                pre-selected from the step-2 country, so it always has a value. */}
            <div className="flex gap-2">
              <Input
                id="hourlyRate"
                type="number"
                min="0"
                max="10000"
                placeholder="e.g., 800"
                {...register("hourlyRate")}
                className={cn(
                  "flex-1 transition-all duration-200",
                  errors.hourlyRate && "border-destructive focus-visible:ring-destructive"
                )}
                disabled={isLoading}
              />
              <select
                id="hourlyRateCurrency"
                aria-label="Currency"
                value={formData.hourlyRateCurrency}
                onChange={event => {
                  const currency = toRateCurrency(event.target.value);
                  if (currency) {
                    onFormChange({ hourlyRateCurrency: currency });
                  }
                }}
                disabled={isLoading}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {RATE_CURRENCIES.map(currency => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
            {errors.hourlyRate && (
              <p className="text-sm text-destructive font-medium">{errors.hourlyRate.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Your expected hourly rate and its currency. The rate for a specific role is agreed in
              that assignment&apos;s currency.
            </p>
          </div>
        </div>

        {/* LinkedIn URL */}
        <div className="space-y-2">
          <Label htmlFor="linkedinUrl" className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            LinkedIn Profile
          </Label>
          <Input
            id="linkedinUrl"
            type="url"
            placeholder={LINKEDIN_PROFILE_EXAMPLE}
            {...register("linkedinUrl")}
            className={cn(
              "transition-all duration-200",
              errors.linkedinUrl && "border-destructive focus-visible:ring-destructive"
            )}
            disabled={isLoading}
          />
          {errors.linkedinUrl && (
            <p className="text-sm text-destructive font-medium">{errors.linkedinUrl.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Optional: Your LinkedIn profile URL for professional credibility
          </p>
        </div>

        {/* Interest in employment (backend#882) — optional. */}
        <div className="flex items-start gap-3 rounded-md border border-gray-200 p-4">
          <input
            id="interestedInEmployment"
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
            checked={formData.interestedInEmployment}
            disabled={isLoading}
            onChange={event => onFormChange({ interestedInEmployment: event.target.checked })}
          />
          <div className="space-y-1">
            <Label htmlFor="interestedInEmployment" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              {EMPLOYMENT_INTEREST_QUESTION}
            </Label>
            <p className="text-xs text-muted-foreground">
              Optional. Tick this if you would consider being hired, not only working as a
              freelancer or subcontractor. You can change it later on your profile.
            </p>
          </div>
        </div>

        {/* CV Upload */}
        {sessionId && (
          <CvUpload
            sessionId={sessionId}
            cvUrl={formData.cvUrl}
            onUploadSuccess={(url, _fileName) => {
              onFormChange({ cvUrl: url || null });
            }}
          />
        )}

        {/* Skills Requirement Message */}
        {formData.selectedSkills.length === 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              Please select at least one skill to continue. Skills help clients find consultants
              with the right expertise.
            </AlertDescription>
          </Alert>
        )}

        {/* Privacy policy + confidentiality acknowledgements (#863) */}
        <SignupAcknowledgements
          documents={consentDocuments}
          isLoading={consentsLoading}
          error={consentsError}
          accepted={accepted}
          disabled={isLoading}
          onChange={(key, checked) => setAccepted(prev => ({ ...prev, [key]: checked }))}
          onRetry={onRetryConsents}
        />

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Info Message */}
        <Alert>
          <User className="h-4 w-4" />
          <AlertDescription>
            <strong>Final Step!</strong> Your profile information and skills will be reviewed by our
            team. Once approved, you'll receive an email with your login credentials and can start
            applying for consultant opportunities.
          </AlertDescription>
        </Alert>
      </form>
    </SignupStep>
  );
};
